import { isNull, isUndefined } from 'lodash';
import { SELECTION_A, SELECTION_B, VALUATIONS_OTHER_LABEL } from 'common/constants/valuations';
import {
  PV_OF_TERMINAL_VALUE_CUSTOM_KEY,
  TERMINAL_VALUE_CUSTOM_KEY,
} from 'pages/Valuations/approaches/discountCashFlow/utilities/constants';
import {
  APPROACH_SELECTIONS_ALIASES,
  EBITDA_APPROACH_SELECTION,
  EBITDA_GPC_MULTIPLE,
  GPC_LTM_EBITDA_COLUMN_LEGEND,
  GPC_LTM_REVENUE_COLUMN_LEGEND,
  GPC_MULTIPLE,
  GPT_EBITDA_COLUMN_LEGEND,
  GPT_REVENUE_COLUMN_LEGEND,
  MULTIPLE_ALIASES,
  PUBLIC_COMPANIES,
  PV_OF_TERMINAL_VALUE,
  REM_TVT_SECOND_COLUMN,
  REVENUE_APPROACH_SELECTION,
  SELECT_MULTIPLE_ALIASES,
  TERMINAL_VALUE,
} from 'pages/Valuations/util/constants';
import { mapVariableLabelsToSelectionIndex } from 'pages/Valuations/util/percentileOptionsTools';
import { getSelectionCellOptions, publicCompsOrTransactionsExists } from 'pages/ValuationsAllocation/util';
import { getValueByPropOrFallback, parseValue } from 'utillities';
import { alphabetGenerator } from 'utillities/alphabet-utilities';
import swapMap from 'utillities/swapMap';
import getCurrentSelectedApproach from './getCurrentSelectedApproach';
import getFormulaExpression from './getFormulaExpression';
import obtainMapForSelectMultipleAlias from './obtainMapForSelectMultipleAlias';

const getValue = (column, row) => {
  if (row.alias === EBITDA_APPROACH_SELECTION) {
    const { ebitda_gpc_approach_selection, ebitda_gpt_approach_selection } = column;
    return ebitda_gpc_approach_selection ?? ebitda_gpt_approach_selection;
  }
  if (row.alias === REVENUE_APPROACH_SELECTION) {
    const { revenue_gpc_approach_selection, revenue_gpt_approach_selection } = column;
    return revenue_gpc_approach_selection ?? revenue_gpt_approach_selection;
  }
  return !isUndefined(column[row.alias]) ? column[row.alias] : null;
};

export function getBenchmarkColumnLegend(benchmarkType, rowAlias, columnLegend) {
  let benchmarkColumnLegend
    = benchmarkType === PUBLIC_COMPANIES ? GPC_LTM_REVENUE_COLUMN_LEGEND : GPT_REVENUE_COLUMN_LEGEND;
  // this happens when we have the Revenue && EBITDA TVT
  if (rowAlias === GPC_MULTIPLE && benchmarkType === PUBLIC_COMPANIES && columnLegend === REM_TVT_SECOND_COLUMN)
    return GPC_LTM_EBITDA_COLUMN_LEGEND;
  if (rowAlias === EBITDA_GPC_MULTIPLE) {
    benchmarkColumnLegend
      = benchmarkType === PUBLIC_COMPANIES ? GPC_LTM_EBITDA_COLUMN_LEGEND : GPT_EBITDA_COLUMN_LEGEND;
  }
  return benchmarkColumnLegend;
}

function getBenchmarkExpression(row, columnLegend, multipleSelection) {
  const { alias, expr, benchmarkType } = row;
  if (MULTIPLE_ALIASES.includes(alias) && multipleSelection !== VALUATIONS_OTHER_LABEL) {
    const blueprint = expr;
    // column legends depend on benchmark type
    const benchmarkColumnLegend = getBenchmarkColumnLegend(benchmarkType, alias, columnLegend);
    if (blueprint) {
      const benchmarkExpression = blueprint.replace(/#/g, `benchmark.${benchmarkColumnLegend}`);
      return benchmarkExpression.replace(/@/g, columnLegend);
    }
  }
  return '';
}

function getExpr(row, columnLegend) {
  return row.expr ? row.expr.replace(/@/g, columnLegend) : '';
}

function getCellValue(pickedSelectionValue, parsedValue) {
  if (!isNull(pickedSelectionValue)) {
    return pickedSelectionValue;
  }
  return parsedValue;
}

function getMultipleSelectionAliasForMultiple(multipleAlias) {
  if (!MULTIPLE_ALIASES.includes(multipleAlias)) return null;
  const invertedMap = swapMap(obtainMapForSelectMultipleAlias());
  return invertedMap.get(multipleAlias);
}

function updateReadOnlyState(cellObject, makeEditable) {
  if (!makeEditable) return cellObject;
  return { ...cellObject, readOnly: !makeEditable };
}
const getExpressionToUse = ({ rowConfigExpression, benchmarkBasedExpression, multipleSelectedIsOther }) => {
  if (multipleSelectedIsOther) return '';
  return benchmarkBasedExpression || rowConfigExpression;
};

const getExpressionForMultiple = ({ approach, alias, columnLegend, selectedMultipleValue }) =>
  getFormulaExpression({
    approach,
    alias,
    columnLegend,
    selectedMultipleValue,
  });

const getParser
  = (cellUpdateMixin, cfTVTRMSheetName, dcfTVTEMSheetName) =>
    ({ columns, rowConfig, tableData }) => {
      const { approach, approaches } = tableData;
      const alphabet = alphabetGenerator([], columns.length);
      let cells = {};

      const handleRow = (column, columnLegend) => (row, rowIndex) => {
        const rowNumber = rowIndex + 1;
        const key = columnLegend + rowNumber;
        const value = getValue(column, row);
        const type = getValueByPropOrFallback(row, 'gridType', null);

        let customKey;

        if (row.alias === PV_OF_TERMINAL_VALUE) {
          customKey = PV_OF_TERMINAL_VALUE_CUSTOM_KEY;
        }
        if (row.alias === TERMINAL_VALUE) {
          customKey = TERMINAL_VALUE_CUSTOM_KEY;
        }

        /* check if selection for the multiple was "Other".
    We don't want a complex expression if that's the case */
        const multipleSelectionAlias = getMultipleSelectionAliasForMultiple(row.alias);
        const transactionCompsOrPublicCompsExists = publicCompsOrTransactionsExists(approaches);
        let multipleSelection = column[multipleSelectionAlias];
        const multipleSelectedIsOther = multipleSelection === VALUATIONS_OTHER_LABEL;
        const benchmarkBasedExpression = getBenchmarkExpression(row, columnLegend, multipleSelection);

        let expression = '';
        const isSecondColumn = columnLegend === REM_TVT_SECOND_COLUMN;
        const selectedApproach = getCurrentSelectedApproach(approaches, approach, isSecondColumn);

        if (MULTIPLE_ALIASES.includes(row.alias)) {
          if ([SELECTION_A, SELECTION_B].includes(multipleSelection)) {
            const approachData = selectedApproach.valuations_approach_gpt ?? selectedApproach.valuations_approach_gpc;
            const percentileNumber
            = multipleSelection === SELECTION_A
              ? approachData.percentile_selection_a
              : approachData.percentile_selection_b;
            multipleSelection = percentileNumber.toString();
          }

          expression = getExpressionForMultiple({
            approach: selectedApproach,
            alias: row.alias,
            columnLegend,
            selectedMultipleValue: multipleSelection,
          });
        } else {
          expression = getExpressionToUse({
            benchmarkBasedExpression,
            rowConfigExpression: getExpr(row, columnLegend),
            multipleSelectedIsOther,
          });
        }

        const parsedValue = parseValue(
        // by passing panel id for the cell value, the select components are happy
          APPROACH_SELECTIONS_ALIASES.includes(row.alias) && selectedApproach ? selectedApproach.panelId : value,
          type,
          null,
          null,
          row.dbType
        );

        const specificApproach = selectedApproach?.valuations_approach_gpc ?? selectedApproach?.valuations_approach_gpt;
        const multipleOptions = getSelectionCellOptions({
          isDCF: true,
          specificApproach,
        });

        const percentileSelections = rowConfig.find(({ alias }) =>
          SELECT_MULTIPLE_ALIASES.includes(alias)
        )?.percentileSelections;
        const pickedSelectionValue = SELECT_MULTIPLE_ALIASES.includes(row.alias)
          ? mapVariableLabelsToSelectionIndex({
            selection: value,
            options: multipleOptions,
            specificApproach: percentileSelections,
          })
          : null;

        let cell = {
          ...row,
          key,
          type,
          alias: getValueByPropOrFallback(row, 'alias', ''),
          className: '',
          customKey,
          columnId: column.id,
          parentColumn: column.parentColumn,
          columnLegend,
          columnOrder: column.order,
          rowNumber,
          expr: expression,
          value: getCellValue(pickedSelectionValue, parsedValue),
        };

        cell = updateReadOnlyState(cell, multipleSelectedIsOther);

        if (cellUpdateMixin) {
          cell = cellUpdateMixin(cell, cfTVTRMSheetName, dcfTVTEMSheetName);
        }

        if (MULTIPLE_ALIASES.includes(cell.alias)) {
          cell = { ...cell, readOnly: !multipleSelectedIsOther };
        }

        if (APPROACH_SELECTIONS_ALIASES.includes(row.alias) && !transactionCompsOrPublicCompsExists) {
          cell = {
            ...cell,
            readOnly: true,
            value: 'N/A',
            dataEditor: undefined,
            valueViewer: undefined,
            forceComponent: false,
          };
        }

        if (!cell.ignoreCell) {
          cells = {
            ...cells,
            [key]: cell,
          };
        }
      };

      // Parse body cells
      columns.forEach((column, columnIndex) => {
        const columnLegend = alphabet[columnIndex];
        rowConfig.forEach(handleRow(column, columnLegend));
      });

      return cells;
    };

export default getParser;
